import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import ModalCaricoOrdiniFornitore from '../../components/ModalCaricoOrdiniFornitore';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { formatQuantity, formatCurrency } from '../../utils/formatters';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { API_DOCS } from './MovimentiMagazzinoPage.api';

const MODULO = 'magazzino.movimenti';

type StatoMovimento = 'BOZZA' | 'CONFERMATO' | 'STORNATO';
type TipoCausale = 'CARICO' | 'SCARICO' | 'TRASFERIMENTO' | 'RETTIFICA';

const TIPO_LABEL: Record<TipoCausale, string> = {
  CARICO: 'Carico',
  SCARICO: 'Scarico',
  TRASFERIMENTO: 'Trasferimento',
  RETTIFICA: 'Rettifica',
};

interface CausaleOption {
  id: number;
  codice: string;
  descrizione: string;
  tipo: TipoCausale;
  aggiornaCmp: boolean;
  isDefault: boolean;
  attivo: boolean;
}

interface DepositoOption {
  id: number;
  nome: string;
  tipo: string;
}

/**
 * Etichetta compatta per un deposito — usata nei select e nelle celle.
 *  • Toglie il prefisso ridondante "Commessa: " dai depositi di commessa
 *    (è già implicito nel tag [COM]).
 *  • Trasforma il tipo in un tag breve in fondo: [FISSO] / [CT] / [COM].
 *  • Tronca a 32 caratteri per non sforare il dropdown aperto del select.
 */
const DEP_TAG: Record<string, string> = { FISSO: 'FISSO', CONTO_TERZI: 'CT', COMMESSA: 'COM' };
function depositoLabel(d: { nome: string; tipo: string } | null | undefined): string {
  if (!d) return '';
  let nome = d.nome;
  if (d.tipo === 'COMMESSA' && nome.startsWith('Commessa: ')) nome = nome.slice('Commessa: '.length);
  if (nome.length > 32) nome = nome.slice(0, 31).trimEnd() + '…';
  const tag = DEP_TAG[d.tipo] || d.tipo;
  return `${nome} [${tag}]`;
}

interface MovimentoTestata {
  id: number;
  numero: number | null;
  anno: number | null;
  data: string;
  stato: StatoMovimento;
  descrizione: string;
  numeroDocumentoEsterno: string | null;
  causale: { id: number; codice: string; descrizione: string; tipo: TipoCausale } | null;
  depositoOrigine: { id: number; nome: string; tipo: string } | null;
  depositoDestinazione: { id: number; nome: string; tipo: string } | null;
  nominativo: { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null } | null;
  commessa: { id: number; numero: string; descrizione: string } | null;
}

interface RigaListaItem {
  rigaId: number;
  ordine: number;
  articolo: { id: number; codice: string; descrizioneBase: string } | null;
  quantita: number | string;
  prezzoUnitario: number | string;
  segno: number;
  note: string | null;
  movimento: MovimentoTestata;
}

function nominativoLabel(n: MovimentoTestata['nominativo']): string {
  if (!n) return '';
  return n.ragioneSociale || [n.nome, n.cognome].filter(Boolean).join(' ') || n.codice;
}

interface FormState {
  causaleId: string;
  data: string;
  depositoOrigineId: string;
  depositoDestinazioneId: string;
  descrizione: string;
  numeroDocumentoEsterno: string;
  dataDocumentoEsterno: string;
  note: string;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

const FORM_VUOTO: FormState = {
  causaleId: '',
  data: todayISO(),
  depositoOrigineId: '',
  depositoDestinazioneId: '',
  descrizione: '',
  numeroDocumentoEsterno: '',
  dataDocumentoEsterno: '',
  note: '',
};

export default function MovimentiMagazzinoPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const navigate = useNavigate();

  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [items, setItems] = useState<RigaListaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<'' | TipoCausale>('');
  // Carico dagli ordini fornitore aperti: si sceglie il fornitore nella modale
  const [modalCaricoOF, setModalCaricoOF] = useState(false);
  const [filtroStato, setFiltroStato] = useState<'' | StatoMovimento>('');
  const [filtroDeposito, setFiltroDeposito] = useState<string>('');
  const [ricerca, setRicerca] = useState('');

  // Lookup per modale + filtri
  const [causali, setCausali] = useState<CausaleOption[]>([]);
  const [depositi, setDepositi] = useState<DepositoOption[]>([]);

  // Stato modale nuova creazione
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!pronta) return;
    if (!societaId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ societaId: String(societaId) });
      if (filtroTipo)     qs.set('tipo', filtroTipo);
      if (filtroStato)    qs.set('stato', filtroStato);
      if (filtroDeposito) qs.set('depositoId', filtroDeposito);
      // @api GET /api/movimenti-magazzino/righe?societaId=&tipo=&stato=&depositoId= -> { success: true, data: [{ rigaId, ordine, articolo, quantita, prezzoUnitario, segno, note, movimento: { id, numero, anno, data, stato, descrizione, causale, depositoOrigine, depositoDestinazione, nominativo, commessa } }] }
      const res = await apiFetch<{ success: boolean; data: RigaListaItem[] }>(`/api/movimenti-magazzino/righe?${qs.toString()}`);
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadLookup = async () => {
    if (!societaId) { setCausali([]); setDepositi([]); return; }
    try {
      // @api GET /api/causali-movimento?societaId=&attivo=true -> { success: true, data: [{ id, codice, descrizione, tipo, aggiornaCmp, isDefault, attivo }] }
      const cRes = await apiFetch<{ success: boolean; data: CausaleOption[] }>(`/api/causali-movimento?societaId=${societaId}&attivo=true`);
      setCausali(cRes.data ?? []);
      // @api GET /api/depositi?societaId= -> { depositi: [{ id, nome, tipo }] }
      const dRes = await apiFetch<{ depositi: DepositoOption[] }>(`/api/depositi?societaId=${societaId}`);
      setDepositi(dRes.depositi ?? []);
    } catch {
      setCausali([]); setDepositi([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, pronta, filtroTipo, filtroStato, filtroDeposito]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLookup(); }, [societaId]);

  const filtered = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    if (!q) return items;
    return items.filter(r =>
      r.movimento.descrizione.toLowerCase().includes(q)
      || (r.movimento.numeroDocumentoEsterno || '').toLowerCase().includes(q)
      || (r.movimento.causale?.codice || '').toLowerCase().includes(q)
      || (r.articolo?.codice || '').toLowerCase().includes(q)
      || (r.articolo?.descrizioneBase || '').toLowerCase().includes(q)
    );
  }, [items, ricerca]);

  // Totale valorizzato delle righe in elenco (somma di qta×prezzo, considera segno)
  const totaleValore = useMemo(
    () => filtered.reduce((acc, r) => acc + Number(r.quantita) * Number(r.prezzoUnitario), 0),
    [filtered],
  );

  const causaleScelta = useMemo<CausaleOption | null>(() => {
    if (!form?.causaleId) return null;
    return causali.find(c => String(c.id) === form.causaleId) || null;
  }, [form?.causaleId, causali]);

  const apriNuovo = () => {
    setForm({ ...FORM_VUOTO });
    setFormError('');
  };
  const chiudi = () => { setForm(null); setFormError(''); };

  const salva = async () => {
    if (!form) return;
    if (!form.causaleId) { setFormError('Seleziona una causale'); return; }
    if (!form.data) { setFormError('Data obbligatoria'); return; }
    if (!form.descrizione.trim()) { setFormError('Descrizione obbligatoria'); return; }

    const tipo = causaleScelta?.tipo;
    if (tipo === 'CARICO' && !form.depositoDestinazioneId) { setFormError('CARICO: deposito destinazione obbligatorio'); return; }
    if (tipo === 'SCARICO' && !form.depositoOrigineId) { setFormError('SCARICO: deposito origine obbligatorio'); return; }
    if (tipo === 'TRASFERIMENTO') {
      if (!form.depositoOrigineId || !form.depositoDestinazioneId) { setFormError('TRASFERIMENTO: entrambi i depositi obbligatori'); return; }
      if (form.depositoOrigineId === form.depositoDestinazioneId) { setFormError('Origine e destinazione devono essere diversi'); return; }
    }
    if (tipo === 'RETTIFICA' && !form.depositoOrigineId) { setFormError('RETTIFICA: deposito obbligatorio'); return; }

    setSaving(true); setFormError('');
    try {
      const payload: any = {
        societaId: Number(societaId),
        causaleId: Number(form.causaleId),
        data: form.data,
        descrizione: form.descrizione.trim(),
        note: form.note.trim() || null,
        numeroDocumentoEsterno: form.numeroDocumentoEsterno.trim() || null,
        dataDocumentoEsterno: form.dataDocumentoEsterno || null,
      };
      if (form.depositoOrigineId)      payload.depositoOrigineId      = Number(form.depositoOrigineId);
      if (form.depositoDestinazioneId) payload.depositoDestinazioneId = Number(form.depositoDestinazioneId);

      // @api POST /api/movimenti-magazzino { societaId, causaleId, data, depositoOrigineId, depositoDestinazioneId, descrizione, numeroDocumentoEsterno, dataDocumentoEsterno, note } -> { success: true, data: { id } }
      const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/movimenti-magazzino', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      chiudi();
      navigate(`/magazzino/movimenti/${res.data.id}`);
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const noSocieta = !societaId;
  const tipo = causaleScelta?.tipo;

  // Opzioni dinamiche per filtri enum
  const depositiOpts = useMemo(
    () => depositi.map(d => ({ value: String(d.id), label: depositoLabel(d) })),
    [depositi],
  );
  const causaliOpts = useMemo(() => {
    const map = new Map<string, { codice: string; tipo: TipoCausale }>();
    for (const r of items) {
      const c = r.movimento.causale;
      if (c && !map.has(c.codice)) map.set(c.codice, { codice: c.codice, tipo: c.tipo });
    }
    return Array.from(map.values()).map(c => ({
      value: c.codice,
      label: `${c.codice} (${TIPO_LABEL[c.tipo]})`,
    }));
  }, [items]);

  const columns = useMemo<BopExcelColumn<RigaListaItem>[]>(() => [
    {
      key: 'movimento',
      header: 'Movimento',
      accessor: (r) => r.movimento.numero != null
        ? `${r.movimento.numero}/${r.movimento.anno}`
        : `#${r.movimento.id}`,
      filter: { type: 'text' },
      width: '120px',
      render: (r) => (
        <>
          {r.movimento.numero != null
            ? <strong>{r.movimento.numero}/{r.movimento.anno}</strong>
            : <span className="bop-text-muted">Bozza #{r.movimento.id}</span>}
          {r.movimento.numeroDocumentoEsterno && (
            <div className="bop-text-muted">Doc. {r.movimento.numeroDocumentoEsterno}</div>
          )}
        </>
      ),
    },
    {
      key: 'data',
      header: 'Data',
      accessor: (r) => r.movimento.data || '',
      filter: { type: 'dateRange' },
      width: '110px',
      render: (r) => new Date(r.movimento.data).toLocaleDateString('it-IT'),
    },
    {
      key: 'causale',
      header: 'Causale',
      accessor: (r) => r.movimento.causale?.codice || '',
      filter: { type: 'enum', options: causaliOpts },
      width: '140px',
      render: (r) => r.movimento.causale ? (
        <>
          <strong>{r.movimento.causale.codice}</strong>
          <div className="bop-text-muted">{TIPO_LABEL[r.movimento.causale.tipo]}</div>
        </>
      ) : <span className="bop-text-muted">—</span>,
    },
    {
      key: 'articolo',
      header: 'Articolo',
      accessor: (r) => r.articolo
        ? `${r.articolo.codice} ${r.articolo.descrizioneBase}`
        : '',
      filter: { type: 'text' },
      render: (r) => r.articolo ? (
        <>
          <strong>{r.articolo.codice}</strong>
          <div className="bop-text-muted">{r.articolo.descrizioneBase}</div>
        </>
      ) : <span className="bop-text-muted">—</span>,
    },
    {
      key: 'quantita',
      header: 'Quantità',
      accessor: (r) => Number(r.quantita) * Number(r.segno),
      filter: { type: 'number' },
      width: '110px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (r) => {
        const segnoNeg = Number(r.segno) === -1;
        return <>{segnoNeg ? '−' : ''}{formatQuantity(Number(r.quantita))}</>;
      },
    },
    {
      key: 'prezzo',
      header: 'Prezzo (€)',
      accessor: (r) => Number(r.prezzoUnitario),
      filter: { type: 'number' },
      width: '110px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (r) => formatCurrency(Number(r.prezzoUnitario)),
    },
    {
      key: 'valore',
      header: 'Valore (€)',
      accessor: (r) => Number(r.quantita) * Number(r.prezzoUnitario),
      filter: { type: 'number' },
      width: '120px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (r) => <strong>{formatCurrency(Number(r.quantita) * Number(r.prezzoUnitario))}</strong>,
    },
    {
      key: 'depositoOrigine',
      header: 'Origine',
      accessor: (r) => r.movimento.depositoOrigine?.id ? String(r.movimento.depositoOrigine.id) : '',
      filter: { type: 'enum', options: depositiOpts },
      render: (r) => r.movimento.depositoOrigine
        ? <span className="bop-text-muted">{r.movimento.depositoOrigine.nome}</span>
        : <span className="bop-text-muted"><em>—</em></span>,
    },
    {
      key: 'depositoDestinazione',
      header: 'Destinazione',
      accessor: (r) => r.movimento.depositoDestinazione?.id ? String(r.movimento.depositoDestinazione.id) : '',
      filter: { type: 'enum', options: depositiOpts },
      render: (r) => r.movimento.depositoDestinazione
        ? <span className="bop-text-muted">{r.movimento.depositoDestinazione.nome}</span>
        : <span className="bop-text-muted"><em>—</em></span>,
    },
    {
      key: 'nominativoCommessa',
      header: 'Nominativo / Commessa',
      accessor: (r) => {
        const n = r.movimento.nominativo ? nominativoLabel(r.movimento.nominativo) : '';
        const c = r.movimento.commessa ? r.movimento.commessa.numero : '';
        return `${n} ${c}`.trim();
      },
      filter: { type: 'text' },
      render: (r) => (
        <>
          {r.movimento.nominativo && <div>{nominativoLabel(r.movimento.nominativo)}</div>}
          {r.movimento.commessa && <div className="bop-text-muted">Commessa {r.movimento.commessa.numero}</div>}
          {!r.movimento.nominativo && !r.movimento.commessa && <span className="bop-text-muted">—</span>}
        </>
      ),
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (r) => r.movimento.stato,
      filter: {
        type: 'enum',
        options: [
          { value: 'BOZZA',      label: 'Bozza' },
          { value: 'CONFERMATO', label: 'Confermato' },
          { value: 'STORNATO',   label: 'Stornato' },
        ],
      },
      width: '110px',
      render: (r) => (
        <span className={`bop-badge ${
          r.movimento.stato === 'CONFERMATO' ? 'bop-badge--success'
          : r.movimento.stato === 'STORNATO'  ? 'bop-badge--danger'
          : 'bop-badge--neutral'
        }`}>
          {r.movimento.stato.charAt(0) + r.movimento.stato.slice(1).toLowerCase()}
        </span>
      ),
    },
  ], [causaliOpts, depositiOpts]);

  const mostraOrigine = tipo === 'SCARICO' || tipo === 'TRASFERIMENTO' || tipo === 'RETTIFICA';
  const mostraDestinazione = tipo === 'CARICO' || tipo === 'TRASFERIMENTO';

  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h2 className="bop-page-title">Movimenti di magazzino</h2>
          <p className="bop-text-muted">
            Libro movimenti — una riga per articolo movimentato. Clicca una riga per aprire il movimento e modificarlo o stornarlo.
          </p>
        </div>
        {canEdit && (
          <div className="bop-flex">
            <button
              className="bop-btn-secondary"
              onClick={() => setModalCaricoOF(true)}
              disabled={noSocieta}
              title="Registra la merce ricevuta partendo dagli ordini fornitore aperti"
            >
              Carico da ordini fornitore
            </button>
            <button className="bop-btn-primary" onClick={apriNuovo} disabled={noSocieta || causali.length === 0}>
              + Nuovo movimento
            </button>
          </div>
        )}
      </div>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
            <option value="">Seleziona società…</option>
            {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <input
          className="bop-input"
          placeholder="Cerca per articolo, causale, descrizione o doc. esterno…"
          value={ricerca}
          onChange={e => setRicerca(e.target.value)}
        />
        <select className="bop-select" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value as any)}>
          <option value="">Tutti i tipi</option>
          {(Object.keys(TIPO_LABEL) as TipoCausale[]).map(t => (
            <option key={t} value={t}>{TIPO_LABEL[t]}</option>
          ))}
        </select>
        <select className="bop-select" value={filtroStato} onChange={e => setFiltroStato(e.target.value as any)}>
          <option value="">Tutti gli stati</option>
          <option value="BOZZA">Bozza</option>
          <option value="CONFERMATO">Confermato</option>
          <option value="STORNATO">Stornato</option>
        </select>
        <select className="bop-select bop-filter-bar__fill" value={filtroDeposito} onChange={e => setFiltroDeposito(e.target.value)}>
          <option value="">Tutti i depositi</option>
          {depositi.map(d => <option key={d.id} value={d.id}>{depositoLabel(d)}</option>)}
        </select>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      {noSocieta ? (
        <div className="bop-card">
          <div className="bop-empty-state">Seleziona una società per visualizzare i movimenti.</div>
        </div>
      ) : (
        <>
          <BopExcelTable<RigaListaItem>
            data={filtered}
            loading={loading}
            rowKey={(r) => r.rigaId}
            onRowClick={(r) => navigate(`/magazzino/movimenti/${r.movimento.id}`)}
            initialSort={{ key: 'data', dir: 'desc' }}
            emptyState={
              <div className="bop-empty-state">
                Nessun movimento presente. {causali.length === 0
                  ? 'Crea prima almeno una causale movimento per poter inserire un movimento.'
                  : 'Clicca "+ Nuovo movimento" per crearne uno.'}
              </div>
            }
            columns={columns}
          />
          {filtered.length > 0 && (
            <div className="mov-mag-total-line">
              Valore totale: <strong>{formatCurrency(totaleValore)}</strong>
            </div>
          )}
        </>
      )}

      {form && (
        <Modal
          title="Nuovo movimento di magazzino"
          onClose={chiudi}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudi}>Annulla</button>
              <button className="bop-btn-primary" onClick={salva} disabled={saving}>
                {saving ? 'Salvo…' : 'Crea e apri scheda'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          <p className="bop-text-muted">
            Compila la testata. Dopo il salvataggio verrai portato alla scheda del movimento per aggiungere le righe e confermarlo.
          </p>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Causale *</label>
              <select
                className="bop-select"
                value={form.causaleId}
                onChange={e => setForm(f => f && { ...f, causaleId: e.target.value, depositoOrigineId: '', depositoDestinazioneId: '' })}
                autoFocus
              >
                <option value="">— Seleziona causale —</option>
                {causali.map(c => (
                  <option key={c.id} value={c.id}>
                    [{TIPO_LABEL[c.tipo]}] {c.codice} — {c.descrizione}
                  </option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data movimento *</label>
              <input
                type="date"
                className="bop-input"
                value={form.data}
                onChange={e => setForm(f => f && { ...f, data: e.target.value })}
              />
            </div>
          </div>

          {tipo && (
            <div className="bop-form-row">
              {mostraOrigine && (
                <div className="bop-form-group">
                  <label className="bop-label">Deposito {tipo === 'TRASFERIMENTO' ? 'origine' : ''} *</label>
                  <select
                    className="bop-select"
                    value={form.depositoOrigineId}
                    onChange={e => setForm(f => f && { ...f, depositoOrigineId: e.target.value })}
                  >
                    <option value="">— Seleziona —</option>
                    {depositi.map(d => <option key={d.id} value={d.id}>{depositoLabel(d)}</option>)}
                  </select>
                </div>
              )}
              {mostraDestinazione && (
                <div className="bop-form-group">
                  <label className="bop-label">Deposito {tipo === 'TRASFERIMENTO' ? 'destinazione' : ''} *</label>
                  <select
                    className="bop-select"
                    value={form.depositoDestinazioneId}
                    onChange={e => setForm(f => f && { ...f, depositoDestinazioneId: e.target.value })}
                  >
                    <option value="">— Seleziona —</option>
                    {depositi.map(d => <option key={d.id} value={d.id}>{depositoLabel(d)}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              value={form.descrizione}
              onChange={e => setForm(f => f && { ...f, descrizione: e.target.value })}
              placeholder="es. Ricevimento DDT 1234 da Caesar, Trasferimento a commessa C-101…"
            />
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Numero documento esterno</label>
              <input
                className="bop-input"
                value={form.numeroDocumentoEsterno}
                onChange={e => setForm(f => f && { ...f, numeroDocumentoEsterno: e.target.value })}
                placeholder="es. DDT 1234"
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data documento esterno</label>
              <input
                type="date"
                className="bop-input"
                value={form.dataDocumentoEsterno}
                onChange={e => setForm(f => f && { ...f, dataDocumentoEsterno: e.target.value })}
              />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={form.note}
              onChange={e => setForm(f => f && { ...f, note: e.target.value })}
            />
          </div>

          <p className="bop-text-muted">
            Nominativo (fornitore/cliente) e commessa potranno essere collegati dalla scheda del movimento.
          </p>
        </Modal>
      )}

      {modalCaricoOF && societaId && (
        <ModalCaricoOrdiniFornitore
          societaId={parseInt(String(societaId), 10)}
          onClose={() => setModalCaricoOF(false)}
          onCreated={(movimentoId) => navigate(`/magazzino/movimenti/${movimentoId}`)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
